import React, { createContext, useContext, useState, useEffect } from 'react';
import { useStaffNotifications } from '../api/notifications/staffNotificationQueries';
import { toast } from 'react-toastify';

const StaffNotificationsContext = createContext(null);

/**
 * Provider component for staff notifications
 */
export const StaffNotificationsProvider = ({ children }) => {
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const {
    staffNotifications,
    isLoading,
    isError,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    removeNotification
  } = useStaffNotifications();

  // Function to toggle the notifications panel
  const toggleNotifications = () => {
    setNotificationsOpen(prev => !prev);
  };

  // Function to close the notifications panel
  const closeNotifications = () => {
    setNotificationsOpen(false);
  };

  // Function to handle click on a notification
  const handleNotificationClick = async (notification) => {
    try {
      // If notification isn't read, mark it as read
      if (!notification.read) {
        await markAsRead(notification.notificationId);
      }

      // If notification has a link, navigate to it
      if (notification.link) {
        window.location.href = notification.link;
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
      toast.error('Could not process notification');
    }
  };

  // Handle all notifications marked as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  // Handle clearing all notifications
  const handleClearAll = async () => {
    try {
      await clearNotifications();
      closeNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  // Handle removing a single notification
  const handleRemoveNotification = async (notificationId, e) => {
    // Prevent the event from bubbling up to parent handlers
    if (e) {
      e.stopPropagation();
    }

    try {
      await removeNotification(notificationId);
    } catch (error) {
      console.error('Error removing notification:', error);
      toast.error('Failed to remove notification');
    }
  };

  // Play notification sound when unread count increases
  useEffect(() => {
    if (unreadCount > 0) {
      try {
        const audio = new Audio('/notification.mp3');
        audio.play().catch(e => {
          // Silent catch - browser may block auto-play
          console.log('Auto-play blocked by browser policy');
        });
      } catch (e) {
        console.log('Notification sound failed to play:', e);
      }
    }
  }, [unreadCount]);

  // Value to be provided by the context
  const value = {
    notifications: staffNotifications || [],
    isLoading,
    isError,
    unreadCount,
    notificationsOpen,
    toggleNotifications,
    closeNotifications,
    handleNotificationClick,
    handleMarkAllAsRead,
    handleClearAll,
    handleRemoveNotification
  };

  return (
    <StaffNotificationsContext.Provider value={value}>
      {children}
    </StaffNotificationsContext.Provider>
  );
};

/**
 * Hook for consuming the StaffNotificationsContext
 */
export const useStaffNotificationsContext = () => {
  const context = useContext(StaffNotificationsContext);
  if (context === null) {
    throw new Error('useStaffNotificationsContext must be used within a StaffNotificationsProvider');
  }
  return context;
};